import React from 'react';

import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CardMedia from '@mui/material/CardMedia';
import { styled } from '@mui/material/styles';
import {
    CardContent,
    Box,
    Typography,
    Stack,
    Tooltip,
    IconButton,
} from '@sunwisesoftware/sunwise-ui';
import { isNull } from 'lodash';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactHookFormFile } from 'common/components/form/RHF';

import { ALLOWED_EXTENSIONS } from '../constants';

const StyledThumbnail = styled(CardMedia)(({ theme }) => ({
    backgroundColor: '#EEF4FA',
    backgroundSize: 'contain',
    borderRadius: '0.55rem',
    height: 218,
    minWidth: 130,
    padding: '0.25rem',
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('xl')]: {
        minHeight: 208,
        minWidth: 130,
    },
    [theme.breakpoints.down('lg')]: {
        minHeight: 109,
        minWidth: 70,
    },
    [theme.breakpoints.down('sm')]: {
        minHeight: 218,
        minWidth: 140,
    },
    '&:hover, &.Mui-focusVisible': {
        zIndex: 1,
        '& .MuiThumbnailBackdrop-root': {
            opacity: 1,
        },
        '& .MuiBackdrop-root': {
            opacity: 0.15,
        },
    },
}));

const Backdrop = styled('span')(({ theme }) => ({
    backgroundColor: theme.palette.common.black,
    borderRadius: '0.55rem',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    transition: theme.transitions.create('opacity'),
    zIndex: 2,
}));

const ThumbnailBackdrop = styled('span')(({ theme }) => ({
    borderRadius: '0.55rem',
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    transition: theme.transitions.create('opacity'),
    zIndex: 3,
}));

const StyledIconButton = styled(IconButton)(({ theme, color }) => ({
    backgroundColor: theme.palette[color].main,
    color: theme.palette[color].contrastText,

    '&:hover': {
        backgroundColor: theme.palette[color].light,
    },
}));

const Form = ({ control, isEnabledEdition, placeholder, setValue }) => {
    const { t } = useTranslation();

    const image_thumbnail = useWatch({ control, name: 'image_thumbnail' });

    const imageThumbnail =
        !isNull(image_thumbnail) && !Array.isArray(image_thumbnail)
            ? image_thumbnail
            : Array.isArray(image_thumbnail)
              ? image_thumbnail[0].preview
              : placeholder;

    return (
        <CardContent>
            <Stack
                alignItems="center"
                direction="column"
                justifyContent="center"
            >
                <Box
                    component="label"
                    htmlFor={isEnabledEdition ? 'thumbnail_image_01' : ''}
                    sx={{
                        cursor: isEnabledEdition ? 'pointer' : 'normal',
                        display: 'block',
                        textAlign: 'center',
                        with: '100%',
                    }}
                >
                    <StyledThumbnail image={imageThumbnail}>
                        {isEnabledEdition && (
                            <>
                                <ThumbnailBackdrop className="MuiThumbnailBackdrop-root">
                                    <Stack
                                        direction="row"
                                        justifyContent="center"
                                        alignItems="center"
                                        spacing={2}
                                    >
                                        <Tooltip title={t('Upload')}>
                                            <StyledIconButton
                                                aria-label="upload"
                                                color="primary"
                                                onClick={() => {
                                                    document
                                                        .getElementById(
                                                            'thumbnail_image_01',
                                                        )
                                                        .click();
                                                }}
                                            >
                                                <AddPhotoAlternateIcon />
                                            </StyledIconButton>
                                        </Tooltip>
                                    </Stack>
                                </ThumbnailBackdrop>
                                <Backdrop
                                    className="MuiBackdrop-root"
                                    htmlFor="thumbnail_image_01"
                                />
                            </>
                        )}
                    </StyledThumbnail>
                </Box>

                <Typography
                    align="center"
                    variant="caption"
                    color="text.secondary"
                    component="div"
                >
                    {t('JPG format')} - 2MB máx
                </Typography>

                <Box sx={{ display: 'none' }}>
                    <ReactHookFormFile
                        accept={ALLOWED_EXTENSIONS}
                        control={control}
                        id="thumbnail_image_01"
                        maxSize={2000000}
                        name="image_thumbnail"
                        placeholder=""
                        setValue={setValue}
                        variant="profile"
                    />
                </Box>
            </Stack>
        </CardContent>
    );
};

Form.propTypes = {
    control: PropTypes.object,
    isEnabledEdition: PropTypes.bool,
    placeholder: PropTypes.string,
    setValue: PropTypes.func,
};

export default Form;
